/**
 * 🎥 UNIVERSAL VIDEO ELEMENT 2025
 * 
 * Dynamisches Video-Element für HeroHeader
 * - YouTube & Vimeo Support
 * - Responsive Width & Height
 * - Border Radius & Styling
 * - Auto-Extraktion der Video-ID aus URL
 */

import React, { useState, useMemo } from 'react';
import VideoFloatingToolbar from './VideoFloatingToolbar';
import './UniversalVideoElement.css';

function appendPx(value) {
    if (typeof value === 'string' && isNaN(value)) {
        return value;
    }
    return `${value}px`;
}

function UniversalVideoElement({
    element,
    isEditMode,
    isMobileLayout,
    onUpdate,
    onSelect,
    colors = {}
}) {
    const [showToolbar, setShowToolbar] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    
    // Extract Video ID from URL
    const getVideoEmbedUrl = (url) => {
        if (!url) return null;
        
        // YouTube URL Patterns
        const youtubePatterns = [
            /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
            /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/
        ];
        
        // Vimeo URL Pattern
        const vimeoPattern = /vimeo\.com\/(\d+)/;
        
        // Check YouTube
        for (const pattern of youtubePatterns) {
            const match = url.match(pattern);
            if (match && match[1]) {
                return `https://www.youtube.com/embed/${match[1]}`;
            }
        }
        
        // Check Vimeo
        const vimeoMatch = url.match(vimeoPattern);
        if (vimeoMatch && vimeoMatch[1]) {
            return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
        }
        
        // If already embed URL, return as is
        if (url.includes('youtube.com/embed') || url.includes('player.vimeo.com')) {
            return url;
        }
        
        return null;
    };
    
    const embedUrl = useMemo(() => getVideoEmbedUrl(element.videoUrl), [element.videoUrl]);
    
    // Responsive Values
    const activeWidth = isMobileLayout 
        ? (element.videoWidthMobile ?? element.videoWidth ?? 100)
        : (element.videoWidth ?? 100);
    
    const activeHeight = isMobileLayout
        ? (element.videoHeightMobile ?? element.videoHeight ?? 400)
        : (element.videoHeight ?? 500);
    
    const activeBorderRadius = isMobileLayout
        ? (element.borderRadiusMobile ?? element.borderRadius ?? 12)
        : (element.borderRadius ?? 12);
    
    // Width Type (% oder px)
    const widthValue = element.videoWidthType === 'px' 
        ? `${activeWidth}px`
        : `${Math.min(activeWidth, 100)}%`;
    
    return (
        <div 
            className="universal-video-element-wrapper"
            style={{
                width: '100%',
                display: 'flex',
                justifyContent: element.videoAlignment || 'center',
                marginTop: appendPx(isMobileLayout ? (element.marginTopMobile ?? element.marginTop ?? 20) : (element.marginTop ?? 20)),
                marginBottom: appendPx(isMobileLayout ? (element.marginBottomMobile ?? element.marginBottom ?? 20) : (element.marginBottom ?? 20)),
                marginLeft: appendPx(isMobileLayout ? (element.marginLeftMobile ?? element.marginLeft ?? 0) : (element.marginLeft ?? 0)),
                marginRight: appendPx(isMobileLayout ? (element.marginRightMobile ?? element.marginRight ?? 0) : (element.marginRight ?? 0))
            }}
            onMouseEnter={() => isEditMode && setShowSettings(true)}
            onMouseLeave={() => isEditMode && setShowSettings(false)}
        >
            <div 
                className={`universal-video-container ${isEditMode ? 'edit-mode' : ''}`}
                style={{
                    width: widthValue,
                    maxWidth: '100%',
                    position: 'relative'
                }}
                onClick={() => isEditMode && onSelect && onSelect()}
            >
                {embedUrl ? (
                    <div 
                        className="video-aspect-ratio-wrapper"
                        style={{
                            position: 'relative',
                            paddingBottom: element.aspectRatio === 'square' ? '100%' : 
                                          element.aspectRatio === '4-3' ? '75%' : '56.25%', // 16:9 default
                            height: element.aspectRatio === 'custom' ? appendPx(activeHeight) : '0',
                            borderRadius: appendPx(activeBorderRadius),
                            overflow: 'hidden',
                            border: element.borderEnabled 
                                ? `${element.borderWidth || 2}px solid ${element.borderColor || '#667eea'}`
                                : 'none',
                            boxShadow: element.shadowEnabled 
                                ? `0 ${element.shadowDistance || 8}px ${element.shadowBlur || 16}px rgba(0, 0, 0, ${(element.shadowOpacity || 25) / 100})`
                                : 'none'
                        }}
                    >
                        <iframe
                            src={embedUrl}
                            title="Video Player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                pointerEvents: isEditMode ? 'none' : 'auto' // Prevent interaction in edit mode
                            }}
                        />
                        
                        {/* Edit Mode Overlay */}
                        {isEditMode && (
                            <div className="video-edit-overlay">
                                <i className="fas fa-video"></i>
                            </div>
                        )}
                    </div>
                ) : (
                    /* Placeholder wenn keine URL */
                    <div 
                        className="video-placeholder"
                        style={{
                            width: '100%',
                            height: appendPx(activeHeight),
                            borderRadius: appendPx(activeBorderRadius),
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(102, 126, 234, 0.1)',
                            border: '2px dashed rgba(102, 126, 234, 0.3)',
                            cursor: 'pointer'
                        }}
                        onClick={() => {
                            setShowToolbar(true);
                            setShowSettings(true);
                        }}
                    >
                        <i className="fas fa-video" style={{ fontSize: '48px', color: 'rgba(102, 126, 234, 0.5)', marginBottom: '12px' }}></i>
                        <p style={{ margin: 0, color: 'rgba(102, 126, 234, 0.7)', fontSize: '14px', fontWeight: '600' }}>
                            Click to add YouTube or Vimeo URL
                        </p>
                    </div>
                )}
                
                {/* Settings Button */}
                {isEditMode && showSettings && (
                    <button
                        className={`video-settings-btn ${showToolbar ? 'active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowToolbar(!showToolbar);
                        }}
                        title="Video Settings"
                    >
                        <i className="fas fa-cog"></i>
                    </button>
                )}
            </div>
            
            {/* Video Floating Toolbar */}
            <VideoFloatingToolbar
                isEditMode={isEditMode}
                isVisible={showToolbar}
                    
                    // Video URL
                    videoUrl={element.videoUrl || ''}
                    
                    // Size
                    videoWidth={element.videoWidth ?? 100}
                    videoWidthMobile={element.videoWidthMobile}
                    videoWidthType={element.videoWidthType || '%'}
                    videoHeight={element.videoHeight ?? 500}
                    videoHeightMobile={element.videoHeightMobile ?? 400}
                    aspectRatio={element.aspectRatio || '16-9'}
                    
                    // Alignment
                    videoAlignment={element.videoAlignment || 'center'}
                    
                    // Styling
                    borderRadius={element.borderRadius ?? 12}
                    borderRadiusMobile={element.borderRadiusMobile}
                    borderEnabled={element.borderEnabled || false}
                    borderWidth={element.borderWidth ?? 2}
                    borderColor={element.borderColor || '#667eea'}
                    
                    // Shadow
                    shadowEnabled={element.shadowEnabled || false}
                    shadowBlur={element.shadowBlur ?? 16}
                    shadowDistance={element.shadowDistance ?? 8}
                    shadowOpacity={element.shadowOpacity ?? 25}
                    
                    // Margins
                    marginTop={element.marginTop ?? 20}
                    marginBottom={element.marginBottom ?? 20}
                    marginLeft={element.marginLeft ?? 0}
                    marginRight={element.marginRight ?? 0}
                    marginTopMobile={element.marginTopMobile}
                    marginBottomMobile={element.marginBottomMobile}
                    marginLeftMobile={element.marginLeftMobile}
                    marginRightMobile={element.marginRightMobile}
                    
                    // Callbacks
                    onStyleChange={(updates) => {
                        if (onUpdate) {
                            onUpdate({ ...element, ...updates });
                        }
                    }}
                    onClose={() => setShowToolbar(false)}
                    colors={colors}
                    isMobileLayout={isMobileLayout}
            />
        </div>
    );
}

export default UniversalVideoElement;

